import { DateTime } from "luxon";

const smartCompare = (valA, valB) => {
  const isNullish = (v) => v === null || v === undefined;
  if (isNullish(valA) && isNullish(valB)) return 0;
  if (isNullish(valA)) return 1;
  if (isNullish(valB)) return -1;

  if (Array.isArray(valA) && Array.isArray(valB)) {
    const len = Math.min(valA.length, valB.length);
    for (let i = 0; i < len; i++) {
      const cmp = smartCompare(valA[i], valB[i]);
      if (cmp !== 0) return cmp;
    }

    return valA.length - valB.length;
  }

  if (typeof valA === "number" && typeof valB === "number") {
    return valA - valB;
  }

  if (valA instanceof Date && valB instanceof Date) {
    return valA.getTime() - valB.getTime();
  }

  // Fallback string comparison
  return valA.toString().localeCompare(valB.toString(), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export default {
  statuses: {
    0: { icon: "text-[#36b59c] bg-[#36b59c]/10", name: "msg.granted" },
    1: { icon: "text-[#e05454] bg-[#e05454]/10", name: "msg.error" },
    2: { icon: "text-[#247aa1] bg-[#247aa1]/10", name: "msg.pending" },
    3: { icon: "text-[#e05454] bg-[#247aa1]/10", name: "msg.agentOffline" },
    4: { icon: "text-[#ccd1c4] bg-[#ccd1c4]/10", name: "msg.waiting" },
    5: { icon: "text-[#36b59c] bg-[#36b59c]/10", name: "msg.read" },
    6: { icon: "text-[#247aa1] bg-[#247aa1]/10", name: "msg.published" },
    8: { icon: "text-[#247aa1] bg-[#247aa1]/10", name: "msg.pending" },
    9: { icon: "text-[#ccd1c4] bg-[#ccd1c4]/10", name: "msg.revoked" },
    10: { icon: "text-[#e05454] bg-[#e05454]/10", name: "msg.error" },
  },
  timeRemaining: (time) => {
    const date = DateTime.fromISO(time);
    const now = DateTime.now();

    return date.diff(now).milliseconds;
  },
  formatDuration: (ms) => {
    if (ms === null || ms === undefined || ms < 0) {
      return "00:00:00";
    }
    const formatNumber = (n) => Math.floor(n).toString().padStart(2, "0");
    return `${formatNumber(ms / (60 * 60 * 1000))}:${formatNumber((ms / (60 * 1000)) % 60)}:${formatNumber((ms / 1000) % 60)}`;
  },
  formatDate: (date) => {
    const d = new Date(date);

    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();

    return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  },
  formatTime: (time) => {
    if (time === null || time === undefined) {
      return `00:00:00`;
    } else {
      const d = new Date(time);

      const h = d.getHours().toString().padStart(2, "0");
      const m = d.getMinutes().toString().padStart(2, "0");
      const s = d.getSeconds().toString().padStart(2, "0");

      return `${h}:${m}:${s}`;
    }
  },
  formatAsDays: (time) => {
    const date = DateTime.fromISO(time);
    const now = DateTime.now();
    const days = Math.floor(date.diff(now).milliseconds / (1000 * 60 * 60 * 24));

    if (days === 1) {
      return "1 day";
    } else if (days === 0) {
      return "today";
    } else if (days == -1) {
      return "1 day ago";
    } else if (days < -1) {
      return `${-days} days ago`;
    } else {
      return `${days} days`;
    }
  },
  marshalDateTime: (date) => {
    if (typeof date === "undefined" || date === "" || date === null) {
      return null;
    }

    return new Date(date).toISOString();
  },
  formatDateTime(date) {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");

    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");

    return `${year}-${month}-${day}T${h}:${m}`;
  },
  logEntryActor: (entry) => {
    var entry = Object.values(entry)[0];
    return entry.UserName || entry.AdminName || "";
  },
  logEntryAction: (entry) => {
    return "logEntryKind." + Object.keys(entry)[0];
  },
  checkJsBundleHash: (v) => {
    var jsBundleHash = localStorage.getItem("jsBundleHash");
    if (!jsBundleHash) {
      localStorage.setItem("jsBundleHash", v);
    } else if (jsBundleHash !== v) {
      localStorage.setItem("jsBundleHash", v);
      window.location.reload(true);
    }
  },
  /**
   * Unwrap error and turn it into something user-friendly
   *
   * @param {object} error
   */
  renderError: (t, error) => {
    if (error.response && error.response.data && error.response.data.Err) {
      const prefix = t(error.response.data.Err);
      if (error.response.data.Extra) {
        return `${prefix}: ${
          error.response.data.Extra.startsWith("err.") ? t(error.response.data.Extra) : error.response.data.Extra
        }`;
      }
      return prefix;
    }

    if (error.message) {
      return error.message;
    }

    return "An internal server error occurred";
  },
  renderLastModified: (lastModified, t) => {
    if (lastModified) {
      return t("msg.renderedLastModified", {
        relativeTime: DateTime.fromISO(lastModified.At).toRelative(),
        name: lastModified.Name,
      });
    } else {
      return t("Unknown");
    }
  },
  btnClass: (theme, hc) => {
    return {
      "bg-theme-teal": theme == "teal" && !hc,
      "bg-theme-teal-hc": theme == "teal" && hc,
      "bg-theme-grape": theme == "grape" && !hc,
      "bg-theme-grape-hc": theme == "grape" && hc,
      "bg-theme-iceberg": theme == "iceberg" && !hc,
      "bg-theme-iceberg-hc": theme == "iceberg" && hc,
      "bg-theme-rose": theme == "rose" && !hc,
      "bg-theme-rose-hc": theme == "rose" && hc,
      "bg-theme-offsite": theme == "offsite" && !hc,
      "bg-theme-offsite-hc": theme == "offsite" && hc,
    };
  },
  sortObjBy: (val) => (obj) => obj[val],
  id: (x) => x,
  smartCompare,
  smartSortFunc: (key) => {
    return (a, b) => smartCompare(key(a), key(b));
  },
  focusFirstError: (node) => {
    // Technically this doesn't always focus the first error, since formkit
    // children aren't garunteed to be in order with conditional rendering.
    // But this is the closest I can get without querying DOM offsets.
    let firstInvalid = null;

    node.walk((child) => {
      const isLeaf = child.children.length === 0;
      const isInvalid = child.ledger.value("blocking");
      if (!firstInvalid && child.props?.id && isInvalid && isLeaf) {
        firstInvalid = child;
      }
    });

    if (firstInvalid?.props?.id) {
      const el = document.getElementById(firstInvalid.props.id);
      el?.focus();
    }
  },
  icons: {},
};
